import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import BarLoaderComponent from 'webapp/components/bar-loader';
import FormGranulationListItemComponent from 'webapp/components/form/granulation/list-item';
import type { Item } from 'webapp/components/form/range/list-base';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

import type Granulation from 'webapp/models-custom/granulation';

export default class FormGranulationPopupComponent extends PopupInnerContextComponent<PopupData.Form.Granulation> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked selectedGranulationId: string | null = null;

	@tracked isLoading = true;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectedGranulationId = this.dateRange?.granulationTypeId ?? null;

		this.analyticsService.event(TrackingEvent.GRANULATION_DROPDOWN_OPENED, {
			origin: this.popupData.trackingOrigin,
		});
		this.args.model.on(PopupEvent.CLOSING, this, this.onPopupClosing);

		this.metricLimitations.updateLimitations();
		void this.metricLimitations.limitationsLoaded?.then(() => {
			this.isLoading = false;
			this.adjustPosition();
		});
	}

	get metricLimitations() {
		return this.popupData.metricLimitations;
	}

	get dateRange() {
		return this.metricLimitations?.dateRange;
	}

	get exceptionIds() {
		return this.popupData.exceptionIds;
	}

	get hideUnsupported() {
		return !this.currentUserService.isLoggedIn;
	}

	get shownOptions() {
		const exceptionIds = this.exceptionIds || [],
			hideUnsupported = this.hideUnsupported;
		return (this.metricLimitations?.granulations || []).filter((item) => {
			return (
				item.id === this.selectedGranulationId ||
				(!exceptionIds.includes(item.id) && !(hideUnsupported && item.isDisabled))
			);
		});
	}

	adjustPosition() {
		setTimeout(() => {
			const popupElement = document.querySelector('.popup-cdr')!;
			if (popupElement) {
				const popupRect = popupElement.getBoundingClientRect();
				const popupBottomOverflow = popupRect.top + popupRect.height - window.innerHeight;

				if (popupBottomOverflow > 0) {
					this.args.model.reposition();
				}
			}
		}, 10);
	}

	onPopupClosing() {
		if (this.dateRange?.granulationTypeId !== this.selectedGranulationId) {
			this.popupData.onChange(this.selectedGranulationId);
		}
	}

	@action
	selectItem(item: Item) {
		this.selectedGranulationId = (item as Granulation).id; // only granulations are listed
		this.close();
	}
	<template>
		{{#if this.isLoading}}
			<div class="loader-ct">
				<BarLoaderComponent />
			</div>
		{{else if this.metricLimitations}}
			<ul>
				{{#each this.shownOptions as |item|}}
					<FormGranulationListItemComponent
						@model={{item}}
						@theme={{@model.theme}}
						@isSelected={{eq this.selectedGranulationId item.id}}
						@onSelect={{this.selectItem}}
					/>
				{{/each}}
			</ul>
		{{/if}}
	</template>
}
